import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ControlPanelHeader.scss';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ThemeSelector from '../ThemeSelector/ThemeSelector';
import BackendService from '../../services/BackendService';
import { LocalDataTrack } from 'twilio-video';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'react-use';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { ControlPanelHeaderModal } from './style';
import { hashCurrentSession } from '../../utils/sessionUUIDToCode';
import KeyIcon from '../../assets/mainControls/IconKey.svg?react';
import SentryTestButton from '../SentryTestButton/SentryTestButton';

import NetworkQualityIndicator from '../UI/NetworkQualityIndicator/NetworkQualityIndicator';
import NetworkReportPopover from '../../helpers/NetworkReport/NetworkReportPopover';
import { runOrReusePreflight } from '../../helpers/NetworkReport/NetworkPreflight';
import { isBadQuality, Quality } from '../../commands/practitioner/enums/CallQuality';
import { metrics } from '../../metrics/sentryMetrics';
import {
  AUTO_RECALL_BEFORE_RESTART_MS,
  AUTO_RECALL_MIN_PRESENCE_AFTER_ARM_MS,
  INITIAL_WAITING_POPUP_DELAY_MS,
  WAITING_FOR_CLIENT_TIMEOUT_MS,
} from '../../utils/consts';

import Tooltip from '../Tooltip/Tooltip';

const hasValidStats = (r: any) => {
  const s = r?.stats;
  return (
    typeof s?.rtt?.average === 'number' &&
    typeof s?.jitter?.average === 'number' &&
    typeof s?.packetLoss?.average === 'number'
  );
};

type TriggerMode = 'auto' | 'manual';

const REUSE_MS = 120_000;

const practitionerQualityTooltipKeyByResult = (quality: Quality): string => {
  switch (quality) {
    case Quality.Excellent:
      return 'quality-test-excellent';
    case Quality.Good:
      return 'quality-test-good';
    case Quality.Fair:
      return 'quality-test-fair';
    case Quality.Poor:
      return 'quality-test-poor';
    case Quality.Error:
    case Quality.Idle:
    case Quality.Testing:
    default:
      return 'quality-test-no-connection';
  }
};

interface ContainerProps {
  practitionerIsConnected: boolean;
  isNetworkReconnecting: boolean;
  clientIsConnected: boolean;
  clientPageIsReady: boolean;
  clientPagePresenceVersion: number;
  clientPagePresenceUpdatedAt: number;
  callHasStarted: boolean;
  setClientIsConnected: (v: boolean) => void;
  setPractitionerIsConnected: (v: boolean) => void;
  connectToRoom: (token: string, track: LocalDataTrack) => void;
  StopCall: () => void;
  sessionId: string;
  treatmentId: string;
  currentState: any;
  showThemeSelector: boolean;
  setWaitingForClient: Dispatch<SetStateAction<boolean>>;
  toggleCallHasStarted: () => void;
  token: string;
  setToken: Dispatch<SetStateAction<string>>;
  callAbortSignal: number;
  userStoppedCall: boolean;
  onRecallRequired: () => void;
  onUserStoppedCall: () => void;
  registerStopCallHandler?: (handler: () => void) => void;
}

const ControlPanelHeader: React.FC<ContainerProps> = ({
  practitionerIsConnected,
  isNetworkReconnecting,
  clientIsConnected,
  clientPageIsReady,
  clientPagePresenceVersion,
  clientPagePresenceUpdatedAt,
  callHasStarted,
  setClientIsConnected,
  setPractitionerIsConnected,
  connectToRoom,
  sessionId,
  treatmentId,
  StopCall,
  currentState,
  showThemeSelector,
  setWaitingForClient,
  toggleCallHasStarted,
  token,
  setToken,
  callAbortSignal,
  userStoppedCall,
  onRecallRequired,
  onUserStoppedCall,
  registerStopCallHandler,
}) => {
  const hasRunInitialTest = useRef(false);
  const baseIconUrl = 'assets/icons/';

  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const { room, localTracks: localVal } = useVideoContext();
  // const [token, setToken] = useState<string>('');

  const [quality, setQuality] = useState<Quality>('idle');
  const [report, setReport] = useState<any | null>(null);
  const [openReport, setOpenReport] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<number>(0);
  const qualityAnchorRef = useRef<HTMLSpanElement | null>(null);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [isAutoRecalling, setIsAutoRecalling] = useState(false);
  const clientIsConnectedRef = useRef(clientIsConnected);
  const practitionerIsConnectedRef = useRef(practitionerIsConnected);
  const isNetworkReconnectingRef = useRef(isNetworkReconnecting);
  const hardDisconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitingPopupDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRecallRunIdRef = useRef(0);
  const clientWasConnectedRef = useRef(false);
  const autoRecallArmedRef = useRef(false);
  const autoRecallPresenceVersionAtArmRef = useRef<number>(0);
  const autoRecallArmedAtRef = useRef<number>(0);
  const autoRecallReasonRef = useRef<'hard_disconnect' | 'client_presence_missing'>('hard_disconnect');
  const hasSeenAbortSignalRef = useRef(false);
  const userStoppedCallRef = useRef(userStoppedCall);

  const {
    isConnected,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    timestamp,
    serialNumber,
  } = currentState || {};

  const { hasDevice } = useAppContext();
  const { onSaveDataModal } = ControlPanelHeaderModal;
  const practitionerHash = hashCurrentSession(sessionId, treatmentId, 'practitioner-hash');

  const deviceMessage = useMemo(() => {
    if (!isConnected) {
      return '';
    }
    return (
      <>
        <p>{headsetWorn ? t('headset-worn') : t('headset-not-worn')}</p>
        <p>
          {t('wifi-quality')}: {wifiQuality}
        </p>
        <p>
          {t('wifi-speed')}: {wifiSpeed} Mbps
        </p>
        <p>
          {t('wifi-quality')}: {batteryHeadset * 100}%
        </p>
        <p>
          {t('battery-controller-1')}: {batteryController1 * 100}%
        </p>
        <p>
          {t('latest-update')}: {new Date(timestamp).toLocaleTimeString()}
        </p>
      </>
    );
  }, [headsetWorn, wifiQuality, wifiSpeed, batteryHeadset, batteryController1, timestamp, isConnected, t]);

  const preflightAbortRef = useRef<AbortController | null>(null);

  const clearHardDisconnectTimeout = useCallback(() => {
    if (hardDisconnectTimeoutRef.current !== null) {
      clearTimeout(hardDisconnectTimeoutRef.current);
      hardDisconnectTimeoutRef.current = null;
    }
  }, []);

  const clearWaitingPopupDelay = useCallback(() => {
    if (waitingPopupDelayRef.current !== null) {
      clearTimeout(waitingPopupDelayRef.current);
      waitingPopupDelayRef.current = null;
    }
  }, []);

  // keep ticking time safely

  useInterval(() => {
    setTotalDuration((n) => n + 1);
  }, 1000);

  // Attach listeners for client connection status indicator.
  useEffect(() => {
    if (!hasRunInitialTest.current) {
      hasRunInitialTest.current = true;
      testCallConnection('auto');
    }
  }, []);

  useEffect(() => {
    if (!room) return;

    const syncClientConnectionFromRoom = () => {
      const hasRemoteParticipants = typeof room.participants?.size === 'number' && room.participants.size > 0;
      setClientIsConnected(hasRemoteParticipants);
    };

    const onConnected = () => syncClientConnectionFromRoom();
    const onDisconnected = () => syncClientConnectionFromRoom();
    const onReconnected = () => syncClientConnectionFromRoom();

    // Sync immediately in case participantConnected happened before listener registration.
    syncClientConnectionFromRoom();

    room.on('participantConnected', onConnected);
    room.on('participantDisconnected', onDisconnected);
    room.on('participantReconnected', onReconnected);
    return () => {
      room.off('participantConnected', onConnected);
      room.off('participantDisconnected', onDisconnected);
      room.off('participantReconnected', onReconnected);
    };
  }, [room, setClientIsConnected]);

  useEffect(() => {
    if (isNetworkReconnecting) {
      clearWaitingPopupDelay();
      setWaitingForClient(false);
      return;
    }

    if (userStoppedCall) {
      clearWaitingPopupDelay();
      setWaitingForClient(false);
      return;
    }

    if (isAutoRecalling) {
      clearWaitingPopupDelay();
      setWaitingForClient(true);
      return;
    }

    const shouldWaitForClient = practitionerIsConnected && !clientIsConnected;
    if (!shouldWaitForClient) {
      clearWaitingPopupDelay();
      setWaitingForClient(false);
      return;
    }

    // After a real disconnect, show popup immediately.
    if (clientWasConnectedRef.current) {
      clearWaitingPopupDelay();
      setWaitingForClient(true);
      return;
    }

    // Initial call startup: avoid flashing popup for fast successful connects.
    if (waitingPopupDelayRef.current !== null) {
      return;
    }

    setWaitingForClient(false);
    waitingPopupDelayRef.current = setTimeout(() => {
      waitingPopupDelayRef.current = null;
      if (!userStoppedCallRef.current && !clientIsConnectedRef.current && !clientWasConnectedRef.current) {
        setWaitingForClient(true);
      }
    }, INITIAL_WAITING_POPUP_DELAY_MS);
  }, [
    clientIsConnected,
    practitionerIsConnected,
    isAutoRecalling,
    isNetworkReconnecting,
    setWaitingForClient,
    userStoppedCall,
    clearWaitingPopupDelay,
  ]);

  useEffect(() => {
    practitionerIsConnectedRef.current = practitionerIsConnected;
  }, [practitionerIsConnected]);

  useEffect(() => {
    isNetworkReconnectingRef.current = isNetworkReconnecting;
  }, [isNetworkReconnecting]);

  useEffect(() => {
    clientIsConnectedRef.current = clientIsConnected;
    if (clientIsConnected) {
      if (clientWasConnectedRef.current === false) {
        metrics.count('call.reconnect.client.connected', 1, { source: 'twilio' });
      }
      clearHardDisconnectTimeout();
      clientWasConnectedRef.current = true;
      autoRecallArmedRef.current = false;
      autoRecallPresenceVersionAtArmRef.current = 0;
      autoRecallArmedAtRef.current = 0;
      autoRecallReasonRef.current = 'hard_disconnect';
    }
  }, [clientIsConnected, clearHardDisconnectTimeout]);

  useEffect(() => {
    userStoppedCallRef.current = userStoppedCall;
    if (userStoppedCall) {
      clearHardDisconnectTimeout();
      clearWaitingPopupDelay();
    }
  }, [userStoppedCall, clearHardDisconnectTimeout, clearWaitingPopupDelay]);

  useEffect(() => {
    if (!practitionerIsConnected) {
      clearHardDisconnectTimeout();
      autoRecallArmedRef.current = false;
      autoRecallPresenceVersionAtArmRef.current = 0;
      autoRecallArmedAtRef.current = 0;
      autoRecallReasonRef.current = 'hard_disconnect';
    }
  }, [practitionerIsConnected, clearHardDisconnectTimeout]);

  useEffect(() => {
    if (!callHasStarted) {
      clearHardDisconnectTimeout();
      clearWaitingPopupDelay();
      clientWasConnectedRef.current = false;
      autoRecallArmedRef.current = false;
      autoRecallPresenceVersionAtArmRef.current = 0;
      autoRecallArmedAtRef.current = 0;
      autoRecallReasonRef.current = 'hard_disconnect';
    }
  }, [callHasStarted, clearHardDisconnectTimeout, clearWaitingPopupDelay]);

  useEffect(() => {
    if (isNetworkReconnecting) {
      clearHardDisconnectTimeout();
      clearWaitingPopupDelay();
      autoRecallArmedRef.current = false;
      autoRecallPresenceVersionAtArmRef.current = 0;
      autoRecallArmedAtRef.current = 0;
      autoRecallReasonRef.current = 'hard_disconnect';
      setIsAutoRecalling(false);
      return;
    }

    if (!practitionerIsConnected) {
      return;
    }

    if (userStoppedCall) {
      autoRecallArmedRef.current = false;
      autoRecallPresenceVersionAtArmRef.current = 0;
      autoRecallArmedAtRef.current = 0;
      autoRecallReasonRef.current = 'hard_disconnect';
      return;
    }

    const shouldArmFromHardDisconnect = clientWasConnectedRef.current;
    const shouldArmFromMissingPresence = !clientWasConnectedRef.current && !clientPageIsReady;

    // Arm auto-recall when:
    // 1) an already-connected client drops from an active call, or
    // 2) call starts while client page is unavailable, then later comes online.
    if (
      !clientIsConnected &&
      !autoRecallArmedRef.current &&
      (shouldArmFromHardDisconnect || shouldArmFromMissingPresence)
    ) {
      autoRecallArmedRef.current = true;
      autoRecallPresenceVersionAtArmRef.current = clientPagePresenceVersion;
      autoRecallArmedAtRef.current = Date.now();
      autoRecallReasonRef.current = shouldArmFromHardDisconnect ? 'hard_disconnect' : 'client_presence_missing';
      if (hardDisconnectTimeoutRef.current === null) {
        hardDisconnectTimeoutRef.current = setTimeout(() => {
          const timeoutReason = autoRecallReasonRef.current;
          if (
            userStoppedCallRef.current ||
            clientIsConnectedRef.current ||
            !practitionerIsConnectedRef.current ||
            isNetworkReconnectingRef.current
          ) {
            return;
          }

          autoRecallArmedRef.current = false;
          autoRecallPresenceVersionAtArmRef.current = 0;
          autoRecallArmedAtRef.current = 0;
          autoRecallReasonRef.current = 'hard_disconnect';
          setIsAutoRecalling(false);
          setIsConnecting(false);
          setWaitingForClient(false);
          setPractitionerIsConnected(false);
          setClientIsConnected(false);
          StopCall();
          onRecallRequired();
          clearHardDisconnectTimeout();
          metrics.count('call.recall.required', 1, { reason: `${timeoutReason}_timeout` });
        }, WAITING_FOR_CLIENT_TIMEOUT_MS);
      }
      metrics.count('call.recall.armed', 1, { reason: autoRecallReasonRef.current });
    }

    if (isAutoRecalling) {
      return;
    }

    const hasNewPresenceSinceArm = clientPagePresenceVersion > autoRecallPresenceVersionAtArmRef.current;
    const minPresenceDelayMs =
      autoRecallReasonRef.current === 'hard_disconnect' ? AUTO_RECALL_MIN_PRESENCE_AFTER_ARM_MS : 0;
    const hasFreshPresenceAfterArm = clientPagePresenceUpdatedAt > autoRecallArmedAtRef.current + minPresenceDelayMs;
    if (
      !autoRecallArmedRef.current ||
      !clientPageIsReady ||
      !hasNewPresenceSinceArm ||
      !hasFreshPresenceAfterArm ||
      clientIsConnected
    ) {
      return;
    }

    // Consume arm to avoid duplicate runs from repeated renders/heartbeats.
    autoRecallArmedRef.current = false;
    const runId = ++autoRecallRunIdRef.current;
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const isCancelled = () =>
      runId !== autoRecallRunIdRef.current || clientIsConnectedRef.current || isNetworkReconnectingRef.current;

    const runAutoRecall = async () => {
      try {
        setIsAutoRecalling(true);
        setIsConnecting(true);
        metrics.count('call.recall.auto_triggered', 1, { reason: autoRecallReasonRef.current });
        // Stop immediately once recall is triggered.
        StopCall();
        setPractitionerIsConnected(false);
        setClientIsConnected(false);

        await sleep(AUTO_RECALL_BEFORE_RESTART_MS);
        if (isCancelled()) return;

        await Promise.resolve(toggleCallHasStarted());
      } catch (error) {
        console.error('[Auto Recall Error]', error);
        metrics.count('call.recall.auto_failed', 1, { reason: 'exception' });
      } finally {
        if (runId === autoRecallRunIdRef.current) {
          setIsConnecting(false);
          setIsAutoRecalling(false);
        }
      }
    };

    void runAutoRecall();
  }, [
    clientPageIsReady,
    clientPagePresenceVersion,
    clientPagePresenceUpdatedAt,
    practitionerIsConnected,
    clientIsConnected,
    isAutoRecalling,
    StopCall,
    toggleCallHasStarted,
    setPractitionerIsConnected,
    setClientIsConnected,
    setWaitingForClient,
    onRecallRequired,
    clearHardDisconnectTimeout,
    userStoppedCall,
    isNetworkReconnecting,
    clearWaitingPopupDelay,
  ]);

  useEffect(() => {
    if (!hasSeenAbortSignalRef.current) {
      hasSeenAbortSignalRef.current = true;
      return;
    }
    clearHardDisconnectTimeout();
    clearWaitingPopupDelay();
    autoRecallRunIdRef.current += 1;
    autoRecallArmedRef.current = false;
    autoRecallPresenceVersionAtArmRef.current = 0;
    autoRecallArmedAtRef.current = 0;
    autoRecallReasonRef.current = 'hard_disconnect';
    setIsAutoRecalling(false);
    setIsConnecting(false);
    metrics.count('call.recall.aborted', 1, { source: 'manual_abort' });
  }, [callAbortSignal, clearHardDisconnectTimeout, clearWaitingPopupDelay]);

  useEffect(() => {
    return () => {
      clearHardDisconnectTimeout();
      clearWaitingPopupDelay();
    };
  }, [clearHardDisconnectTimeout, clearWaitingPopupDelay]);

  useEffect(() => {
    if (token && Array.isArray(localVal) && localVal.length > 0) {
      const localDataTrack = localVal.find((track) => track.kind === 'data') as LocalDataTrack | undefined;
      if (localDataTrack) {
        connectToRoom(token, localDataTrack);
        setToken('');
      }
    }
  }, [localVal, token, connectToRoom, setToken]);

  const testCallConnection = async (mode: TriggerMode = 'auto') => {
    if (document.hidden) {
      const onVisible = () => {
        document.removeEventListener('visibilitychange', onVisible);
        testCallConnection(mode);
      };
      document.addEventListener('visibilitychange', onVisible, { once: true });
      return;
    }

    const existing = preflightAbortRef.current;
    if (existing) existing.abort();

    const ac = new AbortController();
    preflightAbortRef.current = ac;

    const isCacheFresh = !!report && Date.now() - lastRunAt < REUSE_MS;
    const willUseCache = mode === 'auto' && isCacheFresh;
    if (!willUseCache) setQuality(Quality.Testing);

    try {
      const result = await runOrReusePreflight({
        sessionId,
        treatmentId,
        getToken: BackendService.getToken,
        durationMs: 10_000,
        recacheMs: REUSE_MS,
        abort: ac.signal,
        cache: mode === 'auto' ? { lastRunAt, report } : undefined,
        tokenKind: 'preflight',
      });

      setReport(result.report);
      setQuality(result.quality);
      setLastRunAt(result.lastRunAt);

      const ok = hasValidStats(result.report);
      setOpenReport(mode === 'manual' ? ok && isBadQuality(result.quality) : ok && result.shouldOpen);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('preflight setup error:', e);
      setReport(null);
      setQuality(Quality.Error);
      setLastRunAt(Date.now());
      setOpenReport(true);
    } finally {
      preflightAbortRef.current = null;
    }
  };

  const copyLink = useCallback(() => {
    const url = `https://emdr-remote.psylaris.nl/clientpage?treatmentUUID=${treatmentId}&sessionUUID=${sessionId}&deviceId=`;
    navigator.clipboard.writeText(url).then(() => {
      setIsSaving(true);
      setTimeout(() => setIsSaving(false), 2000);
    });
  }, [sessionId, treatmentId]);

  const startCall = () => {
    metrics.count('call.action.start_manual', 1);
    toggleCallHasStarted();
  };

  const stopCall = () => {
    onUserStoppedCall();
    autoRecallRunIdRef.current += 1;
    autoRecallArmedRef.current = false;
    autoRecallPresenceVersionAtArmRef.current = 0;
    autoRecallArmedAtRef.current = 0;
    autoRecallReasonRef.current = 'hard_disconnect';
    setIsAutoRecalling(false);
    setWaitingForClient(false);
    StopCall();
    setPractitionerIsConnected(false);
    setClientIsConnected(false);
    setIsConnecting(false);
    metrics.count('call.action.stop_manual', 1);
  };

  useEffect(() => {
    if (typeof registerStopCallHandler !== 'function') {
      return;
    }

    registerStopCallHandler(stopCall);

    return () => {
      registerStopCallHandler(() => {});
    };
  }, [registerStopCallHandler, stopCall]);

  const renderCallOptions = useMemo(() => {
    if (isConnecting) {
      return (
        <Tooltip title={t('connecting') as string}>
          <button className="loading-button">
            <div />
            <div />
            <div />
          </button>
        </Tooltip>
      );
    }

    if (!callHasStarted) {
      return (
        <Tooltip title={t('start-call') as string}>
          <button className="call-button" onClick={startCall}>
            <img src="assets/icons/start-call.svg" alt="" />
          </button>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t('stop-call') as string}>
        <button className="call-button stop" onClick={stopCall}>
          <img src="assets/icons/stop-call.svg" alt="" />
        </button>
      </Tooltip>
    );
  }, [callHasStarted, t, isConnecting, startCall, stopCall]);

  const feedbackUrl = useMemo(() => {
    const baseURL = 'https://psylaris.com/';
    switch (language) {
      case 'nl':
        return `${baseURL}/feedback`;
      case 'de':
        return `${baseURL}/d/feedback`;
      case 'en':
      default:
        return `${baseURL}/en/feedback`;
    }
  }, [language]);

  const practitionerStatusIcon = practitionerIsConnected ? 'connected-practitioner.svg' : 'ready-practitioner.svg';
  const clientStatusIcon = clientIsConnected
    ? 'connected-user.svg'
    : clientPageIsReady
      ? 'ready-user.svg'
      : 'disconnected-user.svg';
  const practitionerQualityTooltip = `${t('practitioner-call-quality-test')} ${t(
    practitionerQualityTooltipKeyByResult(quality)
  )}`;

  return (
    <div className="header">
      <div className="connection-info">
        {renderCallOptions}

        <Tooltip title={t('status-icon-practitioner') as string}>
          <img src={`${baseIconUrl}${practitionerStatusIcon}`} alt="" />
        </Tooltip>

        <Tooltip placement="right-end" title={t('status-icon-client') as string}>
          <span className="tooltip-anchor">
            <img src={`${baseIconUrl}${clientStatusIcon}`} alt="" />
          </span>
        </Tooltip>

        {hasDevice ? (
          <Tooltip
            placement="right-end"
            title={<>{isConnected ? deviceMessage : `${t('device-not-connected')}: ${serialNumber || ''}`}</>}
          >
            <span className="tooltip-anchor">
              <img src={`${baseIconUrl}${isConnected ? 'device_connected.svg' : 'device_not_connected.svg'}`} alt="" />
            </span>
          </Tooltip>
        ) : null}
      </div>

      <div className="session-info-box">
        <Tooltip placement="left" title={t('total-session-duration') as string}>
          <span className="tooltip-anchor">
            <div className="session-info">
              <img src="assets/icons/clock.svg" alt="" />
              <p>{Math.round(totalDuration / 60 || 0)} min.</p>
            </div>
          </span>
        </Tooltip>

        <Tooltip placement="left" title={t('sessionID') as string}>
          <span className="tooltip-anchor">
            <div className="hash-container" onClick={copyLink}>
              <KeyIcon className="icon-hash" />
              {practitionerHash}
              {isSaving && <p className="copied-notification">{t('successfully-copied')}</p>}
            </div>
          </span>
        </Tooltip>
      </div>

      <div className="info-bar">
        {/*<a href={feedbackUrl} target="_blank" rel="noreferrer" data-tooltip={t('info') as string}>*/}
        {/*  <img src={'assets/icons/info-icon.svg'} alt="" />*/}
        {/*</a>*/}
        <span ref={qualityAnchorRef} className="quality-report-anchor">
          <NetworkQualityIndicator
            quality={quality}
            title={practitionerQualityTooltip}
            onClick={() => testCallConnection('manual')}
            size="small"
            disabled={quality === Quality.Testing}
          />
        </span>

        <NetworkReportPopover
          open={openReport && !!report}
          anchorEl={qualityAnchorRef.current}
          quality={quality}
          report={report}
          onClose={() => {
            setOpenReport(false);
            setReport(null);
          }}
        />

        {showThemeSelector ? <ThemeSelector /> : null}
        {/*<SentryTestButton />*/}
        <LanguageSelector />
      </div>
    </div>
  );
};

export default ControlPanelHeader;
